// src/ui/shell/ProspectShell.tsx (T1): the household's board shell in Direction D (docs/design/d, the @side,
// @topbar, @mtop, @tabbar and @foot partials).
//   Desktop (1024px and up): the left sidebar (the builder's logo left-aligned at 64px, the project switcher,
//   Home, Messages, People, then Rooms with thumbnails and counts, and the account row), and the top bar
//   (breadcrumbs, the Reacting-as pill, Invite, Add Photos).
//   Phone and tablet: the top bar (44px logo, project name, the Reacting-as switch) and the bottom tab bar
//   (Home, Rooms, Add Photos, Messages, People).
// Presentational: T5's prospect/Shell.tsx fills the props from the project store. The screen renders its own
// <main> inside (use <Page>). Root class: hb-shell. In the demo, `?add=photos` opens Add Photos (the tour's photos stop), and
// the demo tour's room stop opens the board's fullest room (src/demo/tour.ts). On the public test ride every Add Photos
// opens the sample library instead of a file picker (src/try/SampleLibrary.tsx; nothing is uploaded there).
import type { ComponentChildren } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { href, Link, route } from "../../app/router";
import { tourRoom } from "../../demo/tour";
import { isTrial, openSamples } from "../../try/trial";
import { titleCase } from "../../lib/format";
import { site, tenant } from "../../state/session";
import { Avatar, type AvatarTone } from "../Avatar";
import { Button, IconButton } from "../Button";
import { cx } from "../cx";
import { Icon, type IconName } from "../Icon";
import { Menu } from "../Menu";
import { Badge } from "../Pill";
import type { WelcomeVideoView } from "../Welcome";
import { WelcomeLink } from "../Welcome";
import { ActingAsPill, ActingAsSwitch, type ActingAsProps } from "./ActingAs";
import { AddPhotosSheet, PHOTO_ACCEPT } from "./AddPhotosSheet";
import { Footer } from "./Footer";
import { DemoStrip, useShellMounted } from "./DemoStrip";
import { SkipLink } from "./SkipLink";
import { TopBar, type Crumb } from "./TopBar";
import { BarBrand, MeRow, NavItem, ProjectCard, SideBrand } from "./parts";

export type ShellRoom = {
  id: string;
  name: string;
  /** A small cover (the room's first photo or idea image); null draws a quiet placeholder. */
  thumb: string | null;
  /** Visible items in the room. */
  count: number;
  /** Someone else added or reacted here since the viewer's last visit. */
  isNew?: boolean;
  /** The Start Your Build answers touched this room (a new board, before any activity): the same navy dot. */
  noted?: boolean;
};
export type ProspectSection = "home" | "rooms" | "room" | "unsorted" | "sort" | "messages" | "people" | "settings";

export type ProspectShellProps = {
  pid: string;
  /** `location` is the short line (phone top bar); `locationLong` the sidebar's fuller one ("Near X · Your land"). */
  project: { title: string; location: string | null; locationLong?: string | null; kicker?: string };
  /** The viewer's boards, for the switcher (shown when there is more than one). */
  projects?: readonly { id: string; title: string }[];
  active: ProspectSection;
  activeRoomId?: string | null;
  rooms: readonly ShellRoom[];
  unsortedCount?: number;
  unread?: number;
  peopleCount?: number;
  me: { name: string; initials: string; tone: AvatarTone; roleLabel: string };
  /** The Reacting-as switch, only when the viewer can act as someone else. */
  acting?: ActingAsProps | null;
  canInvite?: boolean;
  /** Enables Add Photos (top bar, tab bar). Omit for viewers who cannot upload (team mode). */
  onAddFiles?: (files: File[], origin: "camera" | null) => void;
  onAddRoom?: () => void;
  crumbs?: readonly Crumb[];
  /** Extra top-bar actions after the Reacting-as pill (Sort's "Finish Later"). */
  topActions?: ComponentChildren;
  /** The folded "Watch ...'s welcome" link, once the card was played or closed. */
  welcome?: { video: WelcomeVideoView; onOpen: () => void } | null;
  /** Strips above the top bar (verify email, team mode). */
  banner?: ComponentChildren;
  /** Team mode: the way back to the Team workspace, in the phone top bar (in place of the account link). */
  back?: { href: string; label: string } | null;
  /** false hides the phone tab bar (Sort owns the bottom edge). */
  tabbar?: boolean;
  footer?: boolean;
  class?: string;
  children?: ComponentChildren;
};

function ProjectSwitch(props: { pid: string; project: ProspectShellProps["project"]; projects?: ProspectShellProps["projects"] }) {
  const { project } = props;
  const inner = <ProjectCard kicker={project.kicker ?? "Design board"} title={project.title} loc={project.locationLong || project.location} />;
  const others = props.projects ?? [];
  if (others.length < 2) return <div class="proj">{inner}</div>;
  return (
    <Menu
      label="Your boards"
      class="proj-menu"
      items={others.map((p) => ({ label: p.title, href: href("home", { pid: p.id }), current: p.id === props.pid }))}
      trigger={(t) => (
        <button type="button" class="proj" {...t} aria-label={`${project.title}. Switch board`}>
          {inner}
          <Icon name="chev-d" />
        </button>
      )}
    />
  );
}

export function ProspectShell(props: ProspectShellProps) {
  const t = tenant.value;
  const { pid, active } = props;
  useShellMounted();
  const [addOpen, setAddOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // The room stop: the room with the most to react to (the first of equals), Kitchen & Dining on a new board.
  const tourTo = props.rooms.reduce<ShellRoom | null>((best, r) => (!best || r.count > best.count ? r : best), null)?.id ?? null;
  useEffect(() => {
    tourRoom.value = tourTo;
  }, [tourTo]);
  const addAsked = !!site.value?.demo && route.value.query.get("add") === "photos";
  const canAdd = Boolean(props.onAddFiles);
  const trial = isTrial();
  const openAdd = () => (trial ? openSamples(pid) : setAddOpen(true));
  useEffect(() => {
    if (addAsked && canAdd) openAdd();
  }, [addAsked, canAdd]);
  const roomList = useRef<HTMLElement>(null);
  // On a short laptop window the whole sidebar scrolls as one column: keep the current room in view.
  useLayoutEffect(() => {
    const side = roomList.current?.closest<HTMLElement>(".side");
    const cur = roomList.current?.querySelector<HTMLElement>(".room-i.is-active");
    if (!side || !cur || side.scrollHeight <= side.clientHeight) return;
    const box = side.getBoundingClientRect();
    const r = cur.getBoundingClientRect();
    if (r.top < box.top || r.bottom > box.bottom) side.scrollTop += r.top - box.top - (box.height - r.height) / 2;
  }, [active, props.activeRoomId]);
  const tabbar = props.tabbar !== false;
  const homeTo = href("home", { pid });
  const roomsTo = href("rooms", { pid });
  const unread = props.unread ?? 0;
  const tabActive = active === "room" || active === "unsorted" ? "rooms" : active === "sort" ? "add" : active;

  const addFiles = props.onAddFiles;
  const onDesktopFiles = (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = "";
    if (files.length && addFiles) addFiles(files, null);
  };

  return (
    <div class={cx("hb-shell", "app", tabbar && "has-tabbar", props.class)} data-shell="prospect">
      <SkipLink />
      <aside class="side" aria-label="Project navigation">
        <SideBrand to={homeTo} />
        <ProjectSwitch pid={pid} project={props.project} projects={props.projects} />
        <nav class="nav" aria-label="Board">
          <NavItem to={homeTo} icon="home" label="Home" current={active === "home"} />
          <NavItem
            to={href("messages", { pid })}
            icon="msg"
            label="Messages"
            current={active === "messages"}
            badge={<Badge count={unread} label={`${unread} unread`} />}
          />
          <NavItem
            to={href("people", { pid })}
            icon="users"
            label="People"
            current={active === "people"}
            badge={props.peopleCount ? <span class="cnt">{props.peopleCount}</span> : null}
          />
        </nav>
        <div class="nav-label">
          <Link to={roomsTo} aria-current={active === "rooms" ? "page" : undefined}>
            Rooms
          </Link>
          {props.onAddRoom ? <IconButton icon="plus" size="sm" label="Add a room" onClick={props.onAddRoom} /> : null}
        </div>
        <nav class="nav nav-rooms" aria-label="Rooms" ref={roomList}>
          {props.rooms.map((r) => {
            const cur = active === "room" && props.activeRoomId === r.id;
            return (
              <Link
                key={r.id}
                class={cx("room-i", cur && "is-active")}
                to={href("room", { pid, rid: r.id })}
                aria-current={cur ? "page" : undefined}
                data-room={r.id}
              >
                {r.thumb ? (
                  <img src={r.thumb} alt="" loading="lazy" />
                ) : (
                  <span class="room-thumb" aria-hidden="true">
                    <Icon name="image-plus" />
                  </span>
                )}
                <span class="room-n">{titleCase(r.name)}</span>
                {r.isNew && !cur ? (
                  <span class="dot" role="img" aria-label="New activity" />
                ) : r.noted && !cur ? (
                  <span class="dot" role="img" aria-label="From your answers" />
                ) : null}
                <span class="cnt">{r.count}</span>
              </Link>
            );
          })}
          {props.unsortedCount ? (
            <Link
              class={cx("room-i", "room-unsorted", active === "unsorted" && "is-active")}
              to={href("unsorted", { pid })}
              aria-current={active === "unsorted" ? "page" : undefined}
            >
              <span class="plus" aria-hidden="true">
                <Icon name="image" />
              </span>
              <span class="room-n">Unsorted</span>
              <span class="cnt">{props.unsortedCount}</span>
            </Link>
          ) : null}
          {props.onAddRoom ? (
            <button type="button" class="room-i add" onClick={props.onAddRoom}>
              <span class="plus" aria-hidden="true">
                <Icon name="plus" />
              </span>
              Add a Room
            </button>
          ) : null}
        </nav>
        <div class="side-foot">
          {props.welcome ? <WelcomeLink video={props.welcome.video} place="side" onOpen={props.welcome.onOpen} /> : null}
          <MeRow to={href("settings", { pid })} current={active === "settings"} initials={props.me.initials} tone={props.me.tone} name={props.me.name} roleLabel={props.me.roleLabel} />
        </div>
      </aside>

      <div class="main">
        <DemoStrip />
        {props.banner}
        <TopBar
          crumbs={props.crumbs ?? [{ label: "Home" }]}
          actions={
            <>
              {props.acting ? <ActingAsPill {...props.acting} /> : null}
              {props.topActions}
              {props.canInvite ? (
                <Button variant="secondary" icon="user-plus" href={href("people", { pid }, { invite: "1" })}>
                  Invite
                </Button>
              ) : null}
              {addFiles ? (
                <>
                  <Button icon="image-plus" onClick={() => (trial ? openSamples(pid) : fileInput.current?.click())}>
                    Add Photos
                  </Button>
                  {trial ? null : (
                    <input ref={fileInput} class="sr" type="file" accept={PHOTO_ACCEPT} multiple tabIndex={-1} aria-hidden="true" onChange={onDesktopFiles} />
                  )}
                </>
              ) : null}
            </>
          }
        />
        <header class="mtop">
          <BarBrand to={homeTo} />
          <div class="mproj">
            <b>{props.project.title}</b>
            {props.project.location ? <span>{props.project.location}</span> : null}
          </div>
          {props.back ? (
            <Link class="mtop-back" to={props.back.href}>
              <Icon name="arrow-l" size="sm" />
              {props.back.label}
            </Link>
          ) : props.acting ? (
            <ActingAsSwitch {...props.acting} />
          ) : (
            // Phone and tablet have no sidebar account row: the viewer's avatar opens Settings (email updates, sign out).
            <Link class="mtop-me" to={href("settings", { pid })} aria-label="Your settings" aria-current={active === "settings" ? "page" : undefined}>
              <Avatar initials={props.me.initials} tone={props.me.tone} size="sm" />
            </Link>
          )}
        </header>
        {props.children}
        {props.footer === false ? null : (
          <Footer
            extra={
              <>
                {props.welcome ? <WelcomeLink video={props.welcome.video} place="foot" onOpen={props.welcome.onOpen} /> : null}
                {props.back ? null : (
                  <Link class="foot-me" to={href("settings", { pid })}>
                    <Avatar initials={props.me.initials} tone={props.me.tone} size="xs" />
                    Your Settings
                  </Link>
                )}
              </>
            }
          />
        )}
      </div>

      {tabbar ? (
        <nav class="tabbar" aria-label="Main" style={{ "--n": String(addFiles ? 5 : 4) }}>
          <TabLink to={homeTo} icon="home" label="Home" current={tabActive === "home"} />
          <TabLink to={roomsTo} icon="rooms" label="Rooms" current={tabActive === "rooms"} />
          {addFiles ? (
            <button type="button" class={cx("tab", "tab-add", tabActive === "add" && "is-active")} onClick={openAdd} aria-haspopup="dialog">
              <span class="ic">
                <Icon name="image-plus" />
              </span>
              Add Photos
            </button>
          ) : null}
          <TabLink
            to={href("messages", { pid })}
            icon="msg"
            label="Messages"
            current={tabActive === "messages"}
            badge={<Badge count={unread} label={`${unread} unread`} />}
          />
          <TabLink to={href("people", { pid })} icon="users" label="People" current={tabActive === "people"} />
        </nav>
      ) : null}
      {addFiles && !trial ? <AddPhotosSheet open={addOpen} onClose={() => setAddOpen(false)} onFiles={addFiles} /> : null}
    </div>
  );
}

export function TabLink(props: { to: string; icon: IconName; label: string; current: boolean; badge?: ComponentChildren }) {
  return (
    <Link class={cx("tab", props.current && "is-active")} to={props.to} aria-current={props.current ? "page" : undefined}>
      <span class="ic">
        <Icon name={props.icon} filled={props.current} />
        {props.badge}
      </span>
      {props.label}
    </Link>
  );
}

/** The page body inside a shell: <main class="page"><div class="wrap">. `id` "main" is the skip-link target. */
export function Page(props: { class?: string; wrapClass?: string; label?: string; children?: ComponentChildren }) {
  return (
    <main class={cx("hb-page", "page", props.class)} id="main" tabIndex={-1} aria-label={props.label}>
      <div class={cx("wrap", props.wrapClass)}>{props.children}</div>
    </main>
  );
}
